import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { parseISODate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { logEvent } from "@/hooks/use-event-log";
import { calculateTimedAbsenceHours } from "@/lib/absence-time";

// "avspasering" finnes i absences-tabellen (uttak av oppspart tid, ofte fra
// Monday-import) og bæres med `hours`. Den opprettes ikke via Syk/Ferie-skjemaet,
// men MÅ vises på månedslukkingen (VIB-1246 Fase 4).
export type AbsenceType = "sick" | "vacation" | "avspasering";
export type AbsenceStatus = "pending" | "approved" | "rejected";

export interface Absence {
  id: string;
  user_id: string;
  type: AbsenceType;
  status: AbsenceStatus;
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  note: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  hours: number | null;
}

export function useAbsences(userId: string | undefined) {
  return useQuery({
    queryKey: ["absences", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("absences")
        .select("*")
        .eq("user_id", userId!)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data as Absence[];
    },
  });
}

/** Fravær med den ansattes navn — lederens oppfølgingsliste (VIB-1653). */
export interface AbsenceWithUser extends Absence {
  users: { full_name: string | null; avatar_url: string | null } | null;
}

/** Periodevalg for lederens fraværsoppfølging. */
export type AbsencePeriod = "current-month" | "previous-month" | "year-to-date";

export const ABSENCE_PERIOD_LABELS: Record<AbsencePeriod, string> = {
  "current-month": "Inneværende måned",
  "previous-month": "Forrige måned",
  "year-to-date": "Hittil i år",
};

/** `[fra, til]` som "YYYY-MM-DD" for et periodevalg, relativt til `now`. */
export function absencePeriodRange(
  period: AbsencePeriod,
  now = new Date(),
): [string, string] {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  if (period === "previous-month") {
    // Dag 0 i inneværende måned = siste dag i forrige måned.
    return [
      iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      iso(new Date(now.getFullYear(), now.getMonth(), 0)),
    ];
  }
  if (period === "year-to-date") {
    return [iso(new Date(now.getFullYear(), 0, 1)), iso(new Date(now.getFullYear(), 11, 31))];
  }
  return [
    iso(new Date(now.getFullYear(), now.getMonth(), 1)),
    iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  ];
}

/**
 * VIB-1653/1734: fravær på tvers av ansatte for lederens Timebank-flate.
 *
 * Leser kun sykdom/ferie (avspasering følger timebank-flyten) i en VALGT
 * periode. Tidligere var det et rullerende 30-dagersvindu — implisitt, umulig
 * å styre, og listen tømtes aldri.
 *
 * Overlapp, ikke inneslutning: et fravær som spenner et månedsskifte vises i
 * BEGGE månedene det berører, siden det er relevant for bemanningen i begge.
 *
 * Perioden holder resultatet trygt under Supabase sitt 1000-radstak uten
 * paginering: 16 ansatte × sykdom/ferie over et år ligger i hundretalls
 * rader, ikke tusener. Vokser staben vesentlig må dette over på
 * `fetchAllPaginated`.
 *
 * queryKey starter med "absences" med vilje — det brede prefikset i
 * CACHE_REGISTRY dekker da denne automatisk når fravær opprettes/endres/slettes.
 */
export function useTeamAbsences(period: AbsencePeriod = "current-month") {
  return useQuery({
    queryKey: ["absences", { scope: "team", period }],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [from, to] = absencePeriodRange(period);
      const { data, error } = await supabase
        .from("absences")
        .select("*, users:user_id(full_name, avatar_url)")
        .in("type", ["sick", "vacation"])
        .neq("status", "rejected")
        .lte("start_date", to)
        .gte("end_date", from)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data as AbsenceWithUser[];
    },
  });
}

/** `{year, month}` for hver kalendermåned et dato-intervall berører (inklusiv). */
export function monthsInRange(startDate: string, endDate: string): { year: number; month: number }[] {
  const start = parseISODate(startDate);
  const end = parseISODate(endDate);
  const months: { year: number; month: number }[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endCursor = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endCursor) {
    months.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

const MONTH_NAMES_NB: Record<number, string> = {
  1: "Januar", 2: "Februar", 3: "Mars", 4: "April", 5: "Mai", 6: "Juni",
  7: "Juli", 8: "August", 9: "September", 10: "Oktober", 11: "November", 12: "Desember",
};

export function useCreateAbsence() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      user_id: string;
      type: AbsenceType;
      start_date: string;
      end_date: string;
      start_time?: string | null;
      end_time?: string | null;
      note?: string;
    }) => {
      const hasTimedRange = Boolean(input.start_time || input.end_time);
      let hours: number | null = null;

      if (hasTimedRange) {
        if (!input.start_time || !input.end_time) {
          throw new Error("Velg både fra- og til-tidspunkt.");
        }
        if (input.start_date !== input.end_date) {
          throw new Error("Deldagsfravær må registreres på én dato.");
        }
        hours = calculateTimedAbsenceHours(input.start_time, input.end_time);
        if (hours == null) {
          throw new Error("Til-tidspunktet må være etter fra-tidspunktet.");
        }
      }

      // Overlapp-vern: samme type + samme bruker + overlappende datoer.
      const { data: overlapping, error: overlapErr } = await supabase
        .from("absences")
        .select("id")
        .eq("user_id", input.user_id)
        .eq("type", input.type)
        .neq("status", "rejected")
        .lte("start_date", input.end_date)
        .gte("end_date", input.start_date)
        .limit(1);
      if (overlapErr) throw overlapErr;
      if (overlapping && overlapping.length > 0) {
        throw new Error(
          "Fraværet overlapper med en eksisterende registrering av samme type i samme periode.",
        );
      }

      // Klientforklaring før databasevernet: også administrator må gjenåpne
      // måneden med begrunnelse før retroaktivt fravær kan endres.
      const months = monthsInRange(input.start_date, input.end_date);
      const years = [...new Set(months.map((m) => m.year))];
      const { data: confirmed, error: confErr } = await supabase
        .from("monthly_confirmations")
        .select("year, month")
        .eq("user_id", input.user_id)
        .in("year", years);
      if (confErr) throw confErr;
      const confirmedSet = new Set((confirmed ?? []).map((c) => `${c.year}-${c.month}`));
      const blockedMonth = months.find((m) => confirmedSet.has(`${m.year}-${m.month}`));
      if (blockedMonth) {
        throw new Error(
          `${MONTH_NAMES_NB[blockedMonth.month]} ${blockedMonth.year} er allerede lukket. En administrator må gjenåpne måneden før fravær kan registreres.`,
        );
      }

      // Both sick leave and vacation are registered directly without an
      // approval flow (product decision: notify, never approve). "pending"
      // and "rejected" only exist in legacy data.
      const status: AbsenceStatus = "approved";
      const { error } = await supabase.from("absences").insert({
        user_id: input.user_id,
        type: input.type,
        start_date: input.start_date,
        end_date: input.end_date,
        start_time: input.start_time ?? null,
        end_time: input.end_time ?? null,
        hours,
        note: input.note,
        status,
      });
      if (error) throw error;

      // Audit: aktør er ALLTID den faktisk innloggede brukeren. Leder som
      // registrerer for en ansatt får on_behalf_of i payload (CLAUDE.md
      // audit-regel 1) — target_user_id er alltid med for klarhet.
      const actorId = authUser?.id;
      if (actorId) {
        logEvent({
          event_type: "absences.created",
          entity_type: "absences",
          user_id: actorId,
          payload: {
            type: input.type,
            start_date: input.start_date,
            end_date: input.end_date,
            start_time: input.start_time ?? null,
            end_time: input.end_time ?? null,
            hours,
            target_user_id: input.user_id,
            ...(actorId !== input.user_id ? { on_behalf_of: input.user_id } : {}),
          },
        });
      }
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "absences", { userId: vars.user_id });
      const message = vars.type === "sick" ? "Sykemelding registrert" : "Ferie registrert";
      toast.success(message);
    },
    onError: (err) => {
      toast.error((err as Error).message || "Noe gikk galt");
    },
  });
}

/**
 * VIB-1733: rett en feilregistrering uten å slette og registrere på nytt.
 *
 * Endrer KUN dato og klokkeslett. Type (Syk/Ferie) er bevisst utelatt — det
 * flytter mellom fraværsformer med ulik betydning, og gjøres ved å slette og
 * registrere på nytt (avklart med Erik 10.08).
 *
 * Serversiden er allerede på plass og gjenbrukes:
 *   - `notif_absence_changed` gir lederen ETT «Fravær endret»-varsel, i
 *     stedet for slett + ny som en omregistrering ville gitt
 *   - `snapshot_absence_on_update` logger gammel/ny til `event_log`
 *   - `enforce_absence_month_lock` blokkerer endring i attestert måned
 */
export function useUpdateAbsence() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      user_id: string;
      type: AbsenceType;
      start_date: string;
      end_date: string;
      start_time?: string | null;
      end_time?: string | null;
    }) => {
      const hasTimedRange = Boolean(input.start_time || input.end_time);
      let hours: number | null = null;

      if (hasTimedRange) {
        if (!input.start_time || !input.end_time) {
          throw new Error("Velg både fra- og til-tidspunkt.");
        }
        if (input.start_date !== input.end_date) {
          throw new Error("Deldagsfravær må registreres på én dato.");
        }
        hours = calculateTimedAbsenceHours(input.start_time, input.end_time);
        if (hours == null) {
          throw new Error("Til-tidspunktet må være etter fra-tidspunktet.");
        }
      } else if (input.end_date < input.start_date) {
        throw new Error("Til-datoen må være etter fra-datoen.");
      }

      // Samme overlapp-vern som ved opprettelse, men uten å telle raden
      // seg selv — ellers ville enhver redigering kollidert med seg selv.
      const { data: overlapping, error: overlapErr } = await supabase
        .from("absences")
        .select("id")
        .eq("user_id", input.user_id)
        .eq("type", input.type)
        .neq("status", "rejected")
        .neq("id", input.id)
        .lte("start_date", input.end_date)
        .gte("end_date", input.start_date)
        .limit(1);
      if (overlapErr) throw overlapErr;
      if (overlapping && overlapping.length > 0) {
        throw new Error(
          "Fraværet overlapper med en eksisterende registrering av samme type i samme periode.",
        );
      }

      const { error } = await supabase
        .from("absences")
        .update({
          start_date: input.start_date,
          end_date: input.end_date,
          start_time: input.start_time ?? null,
          end_time: input.end_time ?? null,
          hours,
        })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "absences", { userId: vars.user_id });
      toast.success("Fravær oppdatert");
    },
    onError: (error) => {
      toast.error((error as Error).message || "Kunne ikke oppdatere fraværet");
    },
  });
}

export function useDeleteAbsence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; user_id: string }) => {
      const { error } = await supabase.from("absences").delete().eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "absences", { userId: vars.user_id });
      toast.success("Fravær slettet");
    },
    onError: (error) => {
      toast.error((error as Error).message || "Kunne ikke slette fraværet");
    },
  });
}
